import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'next-i18next';

import { CompanyActions, CompanySectionGroup, CompanySidebar } from 'components/company-page';
import { OrganizationPageLayout, SimilarOrganizations } from 'components/organization-page';
import Link from 'components/Link';
import { UserActionEnum } from 'hooks/useCompany/useCompanyInternal';
import { useResponsive } from 'hooks/useResponsive';
import { useCompany } from 'lib/providers/CompanyProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { OrganizationTypeEnum } from 'lib/models/organization';
import { getUserAvatarFields } from 'utils/user';

import styles from './Company.module.scss';

export const Company = ({ className }: { className?: string }) => {
  const { company, similarCompanies, updateUserAction } = useCompany();
  const { isLoggedIn, isInitialAuthLoading } = useAuth();
  const [t] = useTranslation();
  const screens = useResponsive();
  const { name, banner_url, address, short_description, is_verified } = company;
  const companyAvatar = useMemo(() => getUserAvatarFields({ company }), [company]);

  const onFollow = useCallback(({ organizationId }: { organizationId: string }) => {
    updateUserAction({ userAction: UserActionEnum.FOLLOW_COMPANY, userActionId: organizationId });
  }, []);

  const onUnfollow = useCallback(({ followingId }: { organizationId: string; followingId: string }) => {
    updateUserAction({ userAction: UserActionEnum.UNFOLLOW_COMPANY, userActionId: followingId });
  }, []);

  const addressLineContent = useMemo(() => {
    if (!address) return null;
    if (is_verified || isLoggedIn || isInitialAuthLoading) return address;

    return (
      <Trans t={t} i18nKey="head.address-line">
        {address} <span className={styles.dot}></span> Work here?{' '}
        <Link href={PAGE_ROUTES.RECRUITER_SIGN_UP} isInteractive>
          Claim profile
        </Link>
      </Trans>
    );
  }, [address, is_verified, isLoggedIn, isInitialAuthLoading]);

  return (
    <OrganizationPageLayout
      organizationName={name}
      shortDescription={short_description}
      addressLineContent={addressLineContent}
      bannerURL={banner_url}
      organizationAvatar={companyAvatar}
      organizationType={OrganizationTypeEnum.COMPANY}
      className={className}
    >
      <CompanyActions />
      <CompanySectionGroup />
      {screens.sm ? (
        <CompanySidebar />
      ) : (
        <SimilarOrganizations
          organizationType={OrganizationTypeEnum.COMPANY}
          organizations={similarCompanies}
          onFollow={onFollow}
          onUnfollow={onUnfollow}
        ></SimilarOrganizations>
      )}
      {screens.sm ? (
        <SimilarOrganizations
          organizationType={OrganizationTypeEnum.COMPANY}
          organizations={similarCompanies}
          onFollow={onFollow}
          onUnfollow={onUnfollow}
        ></SimilarOrganizations>
      ) : (
        <CompanySidebar />
      )}
    </OrganizationPageLayout>
  );
};

Company.displayName = 'Company';
